import React, {useState} from 'react';
import {useLocation, useNavigate} from "react-router-dom";
import {IconChevronDown, IconChevronUp} from "../../../../components/icons";

/**
 * MenuItem
 *
 * Ce composant affiche un menu, à utiliser **dans le sidebar**,
 * qui agira comme un lien au clic ou comme un dropdown contenant d'autre MenuItem.
 * On peut passer des données en state avec path mais en tant que tableau.
 *
 * - children : Permet d'utiliser le composant comme un dropdown, mais l'utilisation devra se faire **sans path** et **subMenu**.
 * - Icon : L'icon que l'on souhaite afficher devant le titre du menu **(src/components/icons.tsx)**
 * - title : Le titre du menu, **à utiliser avec toggle** pour cacher le titre quand le sidebar est réduit
 * - active : Le mot requis dans l'url pour que le menu s'active
 * - path : Le lien vers la page que le clic redirige. Peut être un **string** ou un **tableau ['string', {state: {}}]**
 * - subMenu : Un booléen non-obligatoire qui réduit la couleur si le menu est actif, **à utiliser uniquement dans SubMenuItem**
 * */
const MenuItem = (
  {
    children,
    Icon,
    title = '',
    active = '',
    path,
    subMenu,
  }: {
    children?: React.ReactNode,
    Icon: any,
    title: string,
    active: string,
    path?: any,
    subMenu?: boolean,
  }
) => {
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const [activeDropdown, setActiveDropdown] = useState<boolean>(pathname.includes(active));

  // Méthode d'obtention du path a rediriger si path est string ou si path est un tableau
  const getPath = () => {
    if (!path) return '';
    return (typeof path === 'string') ? path : path[0];
  }

  // Méthode d'obtention du state a mettre dans le path si path est un tableau
  const getPathState = () => {
    if (!path) return '';
    return (typeof path !== 'string' && path[1]) ? path[1] : {};
  }

  // Méthode de gestion du click sur le bouton
  const handleClick = () => {
    return (path) ? navigate(getPath(), getPathState()) : setActiveDropdown(!activeDropdown);
  }

  // Méthode d'ajout de l'activation du bouton
  const isActive = (path: string): string => {
    return pathname.includes(path) ? `active ${subMenu ? 'sub-menu' : ''} text-green-400` : `text-gray-300 hover:text-white`;
  };

  return (
    <>
      <button
        onClick={() => handleClick()}
        className={`menu-item w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left ${isActive(active)}`}
      >
        {(children && !path) ? (
          <div className="w-full flex justify-between">
            <div className="flex items-center gap-2.5">
              {Icon && <Icon className="w-4 h-4"/>}
              {title && (
                <span className="text-sm font-medium text-nowrap min-w-0 overflow-hidden">
                  {title}
                </span>
              )}
            </div>
            {title && (
              <div className="flex items-center">
                {activeDropdown ? <IconChevronUp className="w-4 h-4"/> : <IconChevronDown className="w-4 h-4"/>}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            {Icon && <Icon className="w-4 h-4"/>}
            {title && (
              <span className={`text-sm ${subMenu ? 'font-light' : 'font-medium'} text-nowrap min-w-0 overflow-hidden`}>
                {title}
              </span>
            )}
          </div>
        )}
      </button>

      {(children && !path && activeDropdown) && (children)}
    </>
  );
};

export default MenuItem;